import React, { useState, useEffect } from 'react';
import { Button, Card, Col, Container, Dropdown, DropdownButton, Form, Modal, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap";
import { BiCalendar, BiXCircle } from "react-icons/bi";
import DateTimePicker from 'react-datetime-picker';
import { PencilIcon } from '@primer/octicons-react'
import { BiPlus, BiTrash } from "react-icons/bi";
import { axiosAuth_DATA_MGMT_API } from '../utils/AxoisUtilComponent';
import { useTranslation } from 'react-i18next';
import classes from './AdminPages.module.css';
import { translateAPIError } from '../utils/apiUtils';
import { formatDate } from '../utils/timeUtils';


const JobAdminDetailsView = (props) => {

  // Utilities
  const { i18n, t } = useTranslation(['common', 'custom']);

  // Initialize with actual JSON obj.  If not, will get error.
  const [jobAttrs, setJobAttrs] = useState({
    name: '',
    description: '',
    recurrence: '',
    frequency: '',
    startTime: '',
    endTime: '',
    service: '',
    stateSetting: '',
    status: '',
    creatorUsername: '',
    timeCreated: '',
    updaterUsername: '',
    timeUpdated: ''
  });

  const [origJobAttrs, setOrigJobAttrs] = useState({});


  // ##### Needs to be fetch through API #####
  const serviceOptions = [
    { key: "DATA_COLLECTOR",  value: "Data collector" },
    { key: "DATA_DISPATCHER", value: "Data dispatcher" }
  ];

  const recurrenceOptions = [
    { key: "ONCE",      value: "ONCE" },
    { key: "RECURRING", value: "RECURRING" }
  ];

  const stateSettingOptions = [
    { key: "JOB_STSET_ACTIVE",    value: "JOB_STSET_ACTIVE" },
    { key: "JOB_STSET_SUSPENDED", value: "JOB_STSET_SUSPENDED" },
    { key: "JOB_STSET_DISABLED",   value: "JOB_STSET_DISABLED" }
  ];


  // View vs Edit Modes
  const [editMode, setEditMode] = useState(false);

  const [errMsg,             setErrMsg]             = useState('');
  const [errMsg_Update,      setErrMsg_Update]      = useState('');

  const [dpTypeList, setDpTypeList] = useState([]);

  const [showDpTypeInfo, setShowDpTypeInfo] = useState(false);
  const [dpTypeSelected, setDpTypeSelected] = useState();

  const [dpTypeAddList, setDpTypeAddList] = useState([]);

  const [unassignedDpTypeList, setUnassignedDpTypeList] = useState();

  const [showAddDpTypeDialog, setShowAddDpTypeDialog] = useState(false);
  const [addDpTypeErrMsg, setAddDpTypeErrMsg] = useState('');


  // UI Functions

  // Initialize state
  useEffect(() => {
    if (props) {
      if (props.objDetails.startTime) {
        props.objDetails.startTimeObj = new Date(props.objDetails.startTime);
      }

      if (props.objDetails.endTime) {
        props.objDetails.endTimeObj = new Date(props.objDetails.endTime);
      }

      setJobAttrs(props.objDetails);
      setOrigJobAttrs(props.objDetails);

      fetchAssociatedDpTypes(props.objDetails.id);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const fetchAssociatedDpTypes = (jobId) => {

    if (!jobId) {
      return;
    }

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_DATA_MGMT_API.get("jobs/" + jobId + "/datapointTypes");

        resolve(res.data);

        if (res.status === 200) {
          setDpTypeList(res.data);
        }

        setErrMsg('');
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });
  }


  const fetchUnassignedDpTypes = () => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_DATA_MGMT_API.get("datapoint-types");

        resolve(res.data);

        if (res.status === 200) {

          const cleanList = res.data.filter(dpType => dpType.code !== '--');

          // Remove datapoint types already assigned to the user
          let dpTypeNamesArray = [];

          for (let i=0; i<dpTypeList.length; i++) {
            dpTypeNamesArray.push(dpTypeList[i].code);
          }

          let unassignedDpTypesArray = [];
          for (let i=0; i<cleanList.length; i++) {
            if (!dpTypeNamesArray.includes(cleanList[i].code)) {
              unassignedDpTypesArray.push(cleanList[i]);
            }
          }

          setUnassignedDpTypeList(unassignedDpTypesArray);
        }

        setErrMsg('');
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });
  }


  const handleEditAttrs = (e) => {
    e.preventDefault();
    setEditMode(true);
  }


  const onAttrsInputChange = (e) => {
    setJobAttrs({ ...jobAttrs, [e.target.name]: e.target.value })
  }

  const onServiceSelect = (key, event) => {
    setJobAttrs({ ...jobAttrs, service: key })
  };

  const onRecurrenceSelect = (key, event) => {
    setJobAttrs({ ...jobAttrs, recurrence: key })
  };

  const onStateSelect = (key, event) => {
    setJobAttrs({ ...jobAttrs, stateSetting: key })
  };

  const onDatepickerChangeStartTime = (value) => {
    let newTime = (new Date(value)).getTime();
    let currentTime = (new Date()).getTime();
    let tmp = {};

    // ### This check is not necessary, since the UI already built guardrail.  But keep it here for now. ###
    if (!value && (value != null) && (newTime < currentTime)) {
      setErrMsg_Update(t("I18N_ERR_Date_must_be_future"));
    } else {
      setErrMsg_Update(t(""));
      tmp.startTimeObj = value;

      if (value && jobAttrs.endTimeObj && (value.getTime() > jobAttrs.endTimeObj.getTime())) {
        tmp.endTimeObj = value;
      }

      setJobAttrs({ ...jobAttrs, ...tmp});
    }
  }

  const onDatepickerChangeEndTime = (value) => {
    let newTime = (new Date(value)).getTime();
    let currentTime = (new Date()).getTime();
    let tmp = {};

    // ### This check is not necessary, since the UI already built guardrail.  But keep it here for now. ###
    if (!value && (value != null) && (newTime < currentTime)) {
      setErrMsg(t("I18N_ERR_Date_must_be_future"));
    } else {
      setErrMsg(t(""));
      tmp.endTimeObj = value;

      if (value && jobAttrs.startTimeObj && (value.getTime() < jobAttrs.startTimeObj.getTime())) {
        tmp.startTimeObj = value;
      }

      setJobAttrs({ ...jobAttrs, ...tmp});
    }
  }

  const handleEditCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setJobAttrs(origJobAttrs);

    setEditMode(false);
  }

  const handleEditSubmit = (e) => {
    e.preventDefault();

    if (!jobAttrs.name || jobAttrs.name.trim().length === 0) {
      setErrMsg_Update(t("I18N_ERR_Need_job_name"));
    } else if (jobAttrs.name.length > 100) {
      setErrMsg(t("I18N_ERR_Max_len_name") + 100);
    } else if (jobAttrs.name !== origJobAttrs.name) {
      validateName();
    } else {
      validateOtherFields();
    }

    return;
  }

  // Since this is async, need to call this first, and check the others when username availability returns
  function validateName() {

    new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_DATA_MGMT_API.get("jobs?name=" + jobAttrs.name);

        resolve(res.data);

        if (res.data.length > 0) {
          setErrMsg_Update(t("I18N_ERR_job_name_taken"));
        } else {
          validateOtherFields();
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg_Update(t(translateAPIError(error)));
          validateOtherFields();
        }
        return;
      }
    });
  }

  function validateOtherFields() {
    let  currentTime = (new Date()).getTime();

    // Validate form inputs
    if (jobAttrs.description.length > 200) {
      setErrMsg(t("I18N_ERR_Max_len_desc") + 200);
    } else if (jobAttrs.service === '') {
      setErrMsg_Update(t("I18N_ERR_Need_service"));
    } else if (jobAttrs.stateSetting === '') {
      setErrMsg_Update(t("I18N_ERR_Need_state"));
    } else if (jobAttrs.recurrence === '') {
      setErrMsg_Update(t("I18N_ERR_Need_recurrence"));
    } else if ((jobAttrs.frequency !== null) && (jobAttrs.frequency !== '') && (!Number.isInteger(Number(jobAttrs.frequency)))) {
      setErrMsg_Update(t("I18N_ERR_Freq_must_be_int"));
    } else if (jobAttrs.startTimeObj && ((jobAttrs.startTimeObj.getTime() - currentTime) > 10 * 356 * 24 * 60 * 60 * 1000)) {
      setErrMsg_Update(t("I18N_Start_time") + t("I18N_ERR_Date_10_yr_in_future_limit"));
    } else if (jobAttrs.endTimeObj   && ((jobAttrs.endTimeObj.getTime()   - currentTime) > 10 * 356 * 24 * 60 * 60 * 1000)) {
      setErrMsg_Update(t("I18N_End_time")   + t("I18N_ERR_Date_10_yr_in_future_limit"));
    } else if (jobAttrs.startTimeObj && jobAttrs.endTimeObj && (jobAttrs.startTimeObj.getTime() > jobAttrs.endTimeObj.getTime())) {
      setErrMsg_Update(t("I18N_ERR_End_time_LE_Start_time"));
    } else {
      setErrMsg_Update('');
      updateJob();
    }
  }

  const updateJob = () => {

    const reqBody = jobAttrs;

    reqBody.startTime  = (jobAttrs.startTimeObj  ? jobAttrs.startTimeObj.toISOString()  : "");
    reqBody.endTime    = (jobAttrs.endTimeObj    ? jobAttrs.endTimeObj.toISOString()    : "");

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_DATA_MGMT_API.put("jobs/" + jobAttrs.id, reqBody);

        resolve(res.data);

        if (res.status === 200) {

          // Restore UI to viewing mode
          setEditMode(false);
          fetchJob(jobAttrs.id);
        }
      } catch (error) {
        setErrMsg_Update(t(translateAPIError(error)));
      }
    });

  }

  const fetchJob = (id) => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_DATA_MGMT_API.get("jobs/" + id);

        resolve(res.data);

        if (   (res.status === 200)
            && (res.data && res.data.length > 0)) {

          if (res.data[0].startTime) {
            res.data[0].startTimeObj = new Date(res.data[0].startTime);
          }

          if (res.data[0].endTime) {
            res.data[0].endTimeObj = new Date(res.data[0].endTime);
          }

          setJobAttrs(res.data[0]);
          setOrigJobAttrs(res.data[0]);
        }

        setErrMsg_Update('');
      } catch (error) {
        setErrMsg_Update(t(translateAPIError(error)));
      }
    });
  }


  const handleClose = (e) => {
    e.preventDefault();

    // Return to list
    props.onUIMode('LIST')
  }



/*
  function handleDpTypeInfo(dpType) {

    setDpTypeSelected(dpType);
    setShowDpTypeInfo(true);
  }
*/

  const handleDpTypeInfoClose = (e) => {
    setDpTypeSelected(null);
    setShowDpTypeInfo(false);
  }


  function handleAddDpTypes() {
    fetchUnassignedDpTypes();
    setShowAddDpTypeDialog(true);
  }

  function handleAddDpTypesClose() {
    setDpTypeAddList([]);
    setShowAddDpTypeDialog(false);
  }

/*
  function handleAddDpTypesSubmit() {
    let dpTypeNames = [];

    if (dpTypeAddList.length === 0) {
      setShowAddDpTypeDialog(false);
      return;
    }

    for (let i=0; i<dpTypeAddList.length; i++) {
      dpTypeNames.push(dpTypeAddList[i]);
    }

    let reqBody = {
          dpTypeNames: dpTypeNames
        };

    return new Promise(async (resolve, reject) => {
      try {

        const res = await axiosAuth_DATA_MGMT_API.post("jobs/" + jobAttrs.id + "/datapointTypes", reqBody);

        resolve(res.data);

        if (res.status === 200) {
          setDpTypeAddList([]);
          setAddDpTypeErrMsg('');
          setShowAddDpTypeDialog(false);
          fetchAssociatedDpTypes(jobAttrs.id);
        } else {
          // ##### NEED TO DECIDE WHAT TO DO #####
        }
      } catch (error) {
        setAddDpTypeErrMsg(t(translateAPIError(error)));
      }
    });
  }
*/


  function handleAddDpTypesSubmit() {
    let dpTypeIds = [];

    if (dpTypeAddList.length === 0) {
      setShowAddDpTypeDialog(false);
      return;
    }

    for (let i=0; i<dpTypeAddList.length; i++) {
      dpTypeIds.push(dpTypeAddList[i]);
    }

    let reqBody = {
          dpTypeIds: dpTypeIds
        };

    return new Promise(async (resolve, reject) => {
      try {

        const res = await axiosAuth_DATA_MGMT_API.post("jobs/" + jobAttrs.id + "/datapointTypes", reqBody);

        resolve(res.data);

        if (res.status === 200) {
          setDpTypeAddList([]);
          setAddDpTypeErrMsg('');
          setShowAddDpTypeDialog(false);
          fetchAssociatedDpTypes(jobAttrs.id);
        } else {
          // ##### NEED TO DECIDE WHAT TO DO #####
        }
      } catch (error) {
        setAddDpTypeErrMsg(t(translateAPIError(error)));
      }
    });
  }


  function handleRemoveDpType(dpType) {

    let reqBody = {
          data: {
             "dpTypeIds": []
          }
        };

    reqBody.data.dpTypeIds.push(dpType.id);

    return new Promise(async (resolve, reject) => {
      try {

        const res = await axiosAuth_DATA_MGMT_API.delete("jobs/" + jobAttrs.id + "/datapointTypes/", reqBody);

        resolve(res.data);

        if (res.status === 200) {
          setErrMsg('');
          fetchAssociatedDpTypes(jobAttrs.id);
        } else {
          // ##### NEED TO DECIDE WHAT TO DO #####
        }
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });
  }

  const handleDpTypeSelect = (e) => {

    if (e.target.checked) {
      // Add dpType to the list
      dpTypeAddList.push(e.target.id);
    } else {
      const index = dpTypeAddList.indexOf(e.target.id);
      if (index > -1) {
        dpTypeAddList.splice(index, 1);
      }
    }

    setDpTypeAddList(dpTypeAddList);
  }


  function renderDpType(dpType, index) {
    return (
      <tr key={index} style={{fontWeight:'300'}}>
{/*
        <td><span style={{cursor:'pointer', color:'SteelBlue'}} onClick={() => handleDpTypeInfo(dpType)}>{dpType.code}</span></td>
*/}
        <td>{dpType.code}</td>
        <td>{dpType.description}</td>
        <td><span style={{cursor:'pointer', color:'SteelBlue'}} onClick={() => handleRemoveDpType(dpType)}><BiTrash size={16} /></span></td>
      </tr>
    )
  }


  function renderAddDpTypes(dpType, index) {
    return (
      <tr key={index} style={{fontWeight:'300'}}>
        <td><Form.Check type="checkbox" id={dpType.id} label="" onChange={handleDpTypeSelect} /></td>
        <td>{dpType.code}</td>
        <td>{dpType.description}</td>
      </tr>
    )
  }


  return (
    <>
      <Card style={{overflow:'auto', border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)',
                    padding:'20px 40px 20px 40px', maxWidth:'2500px', width:'100%', fontSize:'13px'}}>

        <div style={{display:'flex'}}>
        <span className={classes.admin_pages__page_title}>
          {t("I18N_Job_Details")}
        </span>
        </div>

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'20px'}}>
          <div display='flex'>
            <span>
              {t("I18N_Job_info")}
            </span>
            {editMode ?
              null
              :
              <span style={{marginLeft:'15px', fontSize:'9px', color:'SteelBlue', cursor:'pointer'}} onClick={handleEditAttrs}>
                <PencilIcon size={12} />
                {t("I18N_Edit")}
              </span>
            }
          </div>
          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

          {editMode ?
            <Container>
              <form>
                <Row className={classes.admin_pages__field_label}>
                  <Col>{t("I18N_Job_name")}: *</Col>
                  <Col>{t("I18N_Description")}:</Col>
                  <Col>{t("I18N_Service")}: *</Col>
                  <Col>{t("I18N_State_setting")}: *</Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>
                    <textarea className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="name"
                              value={jobAttrs.name} onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <textarea className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="description"
                              value={jobAttrs.description} onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                      title={!jobAttrs.service ? t("common:I18N_DROPDOWN_PROMPT") :
                              (serviceOptions.find( ({ key }) => key === jobAttrs.service )).value}
                      onSelect={onServiceSelect}>
                      {serviceOptions.map((item, index) => {
                        return (
                          <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                            {item.value}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </Col>
                  <Col>
                    <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                      title={!jobAttrs.stateSetting ? t("common:I18N_DROPDOWN_PROMPT") :
                              t("I18N_" + (stateSettingOptions.find( ({ key }) => key === jobAttrs.stateSetting )).value)}
                      onSelect={onStateSelect}>
                      {stateSettingOptions.map((item, index) => {
                        return (
                          <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                            {t("I18N_" + item.value)}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                  <Col>{t("I18N_Recurrence")}: *</Col>
                  <Col>{t("I18N_Frequency")}:</Col>
                  <Col>{t("I18N_Start_time")}: </Col>
                  <Col>{t("I18N_End_time")}: </Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col>
                    <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                      title={!jobAttrs.recurrence ? t("common:I18N_DROPDOWN_PROMPT") :
                              t("I18N_" + (recurrenceOptions.find( ({ key }) => key === jobAttrs.recurrence )).value)}
                      onSelect={onRecurrenceSelect}>
                      {recurrenceOptions.map((item, index) => {
                        return (
                          <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                            {t("I18N_" + item.value)}
                          </Dropdown.Item>
                        );
                      })}
                    </DropdownButton>
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="frequency"
                           value={jobAttrs.frequency ? jobAttrs.frequency : ""} onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
{/*
                  <Col>
                    <input className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="startTime"
                           value={jobAttrs.startTime} onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col>
                    <input className={classes.admin_pages__field_editable} style={{minWidth: '100%'}} name="endTime"
                           value={jobAttrs.endTime} onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
*/}
                  <Col>
                    <DateTimePicker onChange={onDatepickerChangeStartTime} value={jobAttrs.startTimeObj}
                       format={ (jobAttrs.startTime != null) ? "y-MM-dd h:00 a" : "y-MM-dd h:00 a" }
/* If encorced here in the UI, the state does not know about it, so will cause a discrepancy.
                       minDate={new Date(3600000 * ( Math.round(Date.now() / 3600000)))}
*/
                       disableClock="true"
                       clearIcon=<BiXCircle size={16} style={{color:"gray"}}/>
                       calendarIcon=<BiCalendar size={16} style={{color:"gray"}}/>
                       locale={i18n.language === 'zh_HK' ? "zh-HK" : (i18n.language === 'zh_CN' ? "zh-CN" : "en")}
                    />
                  </Col>
                  <Col>
                    <DateTimePicker onChange={onDatepickerChangeEndTime} value={jobAttrs.endTimeObj}
                       format={ (jobAttrs.endTimeObj != null) ? "y-MM-dd h:00 a" : "y-MM-dd h:00 a" }
/* If encorced here in the UI, the state does not know about it, so will cause a discrepancy.
                       minDate={ jobAttrs.startTimeObj ? jobAttrs.startTimeObj : new Date(3600000 * ( Math.round(Date.now() / 3600000)))}
*/
                       disableClock="true"
                       clearIcon=<BiXCircle size={16} style={{color:"gray"}}/>
                       calendarIcon=<BiCalendar size={16} style={{color:"gray"}}/>
                       locale={i18n.language === 'zh_HK' ? "zh-HK" : (i18n.language === 'zh_CN' ? "zh-CN" : "en")}
                    />
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{marginTop:'10px', fontStyle:'italic'}}>
                  <span>{t("I18N_Req_fields")}</span>
                </Row>

                <Row>
                  <div display='flex' style={{marginTop:'15px'}}>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={handleEditCancel}>
                      {t("I18N_Cancel")}
                    </Button>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ marginLeft: '15px' }}
                      onClick={handleEditSubmit}>
                      {t("I18N_Save")}
                    </Button>
                    <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                      {errMsg_Update}
                    </span>
                  </div>
                </Row>
              </form>
            </Container>
            :
            <Container>
              <Row className={classes.admin_pages__field_label}>
                <Col>{t("I18N_Job_name")}:</Col>
                <Col>{t("I18N_Description")}:</Col>
                <Col>{t("I18N_Service")}:</Col>
                <Col>{t("I18N_State_setting")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col style={{maxHeight:'80px', overflow:'auto', flexWrap:'wrap', wordWrap:'break-word'}}>
                  {jobAttrs.name}
                </Col>
                <Col style={{maxHeight:'80px', overflow:'auto', flexWrap:'wrap', wordWrap:'break-word'}}>
                  {jobAttrs.description}
                </Col>
                <Col>
                  {(serviceOptions.find( ({ key }) => key === jobAttrs.service )) ?
                   (serviceOptions.find( ({ key }) => key === jobAttrs.service )).value : ''}
                </Col>
                <Col>{t("I18N_" + jobAttrs.stateSetting)}</Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Recurrence")}:</Col>
                <Col>{t("I18N_Frequency")}:</Col>
                <Col>{t("I18N_Start_time")}: </Col>
                <Col>{t("I18N_End_time")}: </Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>{t("I18N_" + jobAttrs.recurrence)}</Col>
                <Col>{jobAttrs.frequency}</Col>
{/*
                <Col>{jobAttrs.startTime}</Col>
                <Col>{jobAttrs.endTime}</Col>
*/}
                <Col>{jobAttrs.startTime ? formatDate(jobAttrs.startTime) : "N/A"}</Col>
                <Col>{jobAttrs.endTime   ? formatDate(jobAttrs.endTime)   : "N/A"}</Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Status")}</Col>
                <Col></Col>
                <Col></Col>
                <Col></Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col style={{color: (jobAttrs.status === 'JOB_ST_RUNNING') ? 'green' : 'red'}}>{t("I18N_" + jobAttrs.status)}</Col>
                <Col></Col>
                <Col></Col>
                <Col></Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Created_by")}:</Col>
                <Col>{t("I18N_Time_created")}:</Col>
                <Col>{t("I18N_Updated_by")}:</Col>
                <Col>{t("I18N_Time_updated")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col style={{overflowWrap:'anywhere'}}>{jobAttrs.creatorUsername}</Col>
                <Col>{formatDate(jobAttrs.timeCreated)}</Col>
                <Col style={{overflowWrap:'anywhere'}}>{jobAttrs.updaterUsername}</Col>
                <Col>{formatDate(jobAttrs.timeUpdated)}</Col>
              </Row>

            </Container>
          }
        </div>


        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'30px'}}>
          <div style={{display:'flex'}}>
            <span>
              {t("I18N_DpType_assoc_with_job")}
            </span>
            <span style={{marginLeft:'auto', marginRight:'20px'}}>
              <OverlayTrigger overlay={<Tooltip id="tooltip-new_dpType" style={{fontSize:'11px'}}>{t("I18N_TIP_Add_datapoint_type")}</Tooltip>}>
                <span onClick={handleAddDpTypes} style={{ marginLeft: '0px', marginRight: '0px', marginTop: '3px', cursor: 'pointer', color: 'rgb(100,100,100)' }}>
                  <BiPlus size={18} />
                </span>
              </OverlayTrigger>
            </span>
          </div>

          <hr className="solid" style={{ margin: '0 0 0 0' }}></hr>

          <div style={{overflow:'auto', maxHeight:'350px', fontSize:'11px', marginTop:'10px'}}>

            <Table hover responsive size="sm">
              <thead style={{color:'#686868', fontSize:'12px', borderBottom:"2px solid #D0D0D0"}}>
                <tr>
                  <th style={{fontWeight:'400'}}>{t("I18N_Datapoint_type_code")}</th>
                  <th style={{fontWeight:'400'}}>{t("I18N_Description")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(dpTypeList && dpTypeList.length > 0) ? dpTypeList.map(renderDpType) : null}
              </tbody>
            </Table>
          </div>
        </div>


        <hr className="solid" style={{ marginTop: '30px' }}></hr>

        <div className={classes.admin_pages__page_subtitle} >
          <Form.Group as={Row} className="mb-2 mt-3">
            <div style={{ display: "flex" }}>
              <Button size="sm" variant="outline-secondary" className="mb-3"
                style={{ marginLeft: '0px', fontSize: '12px', fontWeight: '300' }}
                onClick={handleClose}>
                {t("I18N_Close")}
              </Button>
              <span className={classes.admin_pages__field_label} style={{ color: 'rgba(200,0,0)', verticalAlign: 'top', marginLeft: '10px' }}>
                {errMsg}
              </span>
            </div>
          </Form.Group>
        </div>


        <Modal show={showDpTypeInfo} onHide={handleDpTypeInfoClose} centered >
          <Modal.Body>
            <div className={classes.admin_pages__page_subtitle} style={{marginTop:'0px'}}>
              <div display='flex'>
                <span>
                  {t("I18N_DpType_info")}
                </span>
              </div>

              <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

              <Container>
                <Row className={classes.admin_pages__field_label}>
                  <Col>{t("I18N_DpType_code")}:</Col>
                  <Col>{t("I18N_Description")}:</Col>
                </Row>
                  {dpTypeSelected ?
                    <>
                      <Row className={classes.admin_pages__field_value}>
                        <Col>{t("custom:I18N_" + dpTypeSelected.code)}</Col>
                        <Col>
                          <textarea readOnly className={classes.admin_pages__field_editable} style={{resize:'none', borderStyle:'none', width:'100%'}}
                                    name="description" value={dpTypeSelected.description} />
                        </Col>
                      </Row>
                    </>
                  :
                    <></>
                  }
              </Container>


              <div display='flex'>
                <span>
                  {t("I18N_Permissions")}
                </span>
              </div>

              <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>
            </div>

            <div>
              <Button size="sm" variant="outline-secondary"
                style={{ float: 'right', marginBottom:'5px', fontSize: '12px', fontWeight: '300' }}
                onClick={handleDpTypeInfoClose}>
                {t("I18N_Close")}
              </Button>
            </div>
          </Modal.Body>
        </Modal>


        <Modal show={showAddDpTypeDialog} onHide={handleAddDpTypesClose} centered >
          <Modal.Body>

            <div className={classes.admin_pages__page_subtitle} style={{marginTop:'0px'}}>
              <div display='flex'>
                <span>
                  {t("I18N_DpTypes")}
                </span>
              </div>

              <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>
            </div>

            <div style={{overflow:'auto', fontSize:'11px', marginTop:'10px', maxHeight:'600px'}}>
              <Table hover responsive size="sm">
                <thead style={{color:'#686868', fontSize:'12px', borderBottom:"2px solid #D0D0D0"}}>
                  <tr>
                    <th></th>
                    <th style={{fontWeight:'400'}}>{t("I18N_Datapoint_type_code")}</th>
                    <th style={{fontWeight:'400'}}>{t("I18N_Description")}</th>
                  </tr>
                </thead>
                <tbody>
                  {unassignedDpTypeList ? unassignedDpTypeList.map(renderAddDpTypes) : null}
                </tbody>
              </Table>
            </div>

            <div display='flex' style={{ marginTop: '15px' }}>
              <Button size="tiny" variant="outline-secondary" className="mb-3"
                style={{ float: 'left' }}
                onClick={handleAddDpTypesClose}>
                {t("I18N_Cancel")}
              </Button>
              <Button size="tiny" variant="outline-secondary" className="mb-3"
                style={{ marginLeft: '15px' }}
                onClick={handleAddDpTypesSubmit}>
                {t("I18N_Add_dpTypes")}
              </Button>
              <span className={classes.admin_pages__field_label} style={{ marginLeft: '30px', color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                {addDpTypeErrMsg}
              </span>
            </div>
          </Modal.Body>
        </Modal>

      </Card>
    </>
  );
}
 
export default JobAdminDetailsView;
